import React, { useCallback, useEffect, useMemo, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import { isLocalDashboard } from "../OrgUsageAnalytics/api";
import {
  fetchLoanRuns,
  fetchPerformanceReport,
  fetchRecentLoans,
} from "./api";
import {
  boundLabel,
  formatMs,
  formatNumber,
  formatTimestamp,
} from "./format";
import "./PcPerformance.css";

const DAY_OPTIONS = [
  { value: 1, label: "Last 1 day" },
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
];

const LOAN_RUN_LIMITS = [10, 25, 50];

const DEFAULT_ENV = isLocalDashboard() ? "local" : "prod";
const ENVS = isLocalDashboard() ? ["local", "dev", "prod"] : ["dev", "prod"];

function stagesObjectToRows(stages) {
  if (!stages) {
    return [];
  }
  if (Array.isArray(stages)) {
    return stages.map((stage) => ({
      stage: stage.stage,
      label: stage.stage,
      count: stage.count,
      avgMs: stage.avgMs,
      p90Ms: stage.p90Ms,
      p95Ms: stage.p95Ms,
      maxMs: stage.maxMs,
      complexity: stage.complexity || null,
    }));
  }

  return Object.entries(stages).map(([stage, stats]) => ({
    stage,
    label: stage,
    count: stats.count,
    avgMs: stats.avgMs,
    p90Ms: stats.p90Ms,
    p95Ms: stats.p95Ms,
    maxMs: stats.maxMs,
    complexity: stats.complexity || null,
  }));
}

function MetricCard({ label, value }) {
  return (
    <div className="pcperf-card">
      <div className="pcperf-card-label">{label}</div>
      <div className="pcperf-card-value">{value}</div>
    </div>
  );
}

function TaskStages({ stages }) {
  const rows = stagesObjectToRows(stages);
  if (!rows.length) {
    return (
      <div className="pcperf-muted">
        No stage timings yet for this task. New checklist runs will populate
        stages after deploy.
      </div>
    );
  }

  return (
    <table className="pcperf-table">
      <thead>
        <tr>
          <th>Stage</th>
          <th>Count</th>
          <th>Avg</th>
          <th>P90</th>
          <th>P95</th>
          <th>Max</th>
          <th>Bound</th>
          <th>Complexity</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((stage) => (
          <tr key={stage.stage}>
            <td>
              <div>{stage.label || stage.stage}</div>
              <div className="pcperf-muted">{stage.stage}</div>
            </td>
            <td>{formatNumber(stage.count, 0)}</td>
            <td>{formatMs(stage.avgMs)}</td>
            <td>{formatMs(stage.p90Ms)}</td>
            <td>{formatMs(stage.p95Ms)}</td>
            <td>{formatMs(stage.maxMs)}</td>
            <td>
              <span className="pcperf-bound">
                {boundLabel(stage.complexity?.bound)}
              </span>
            </td>
            <td>
              <div>{stage.complexity?.bigO || "—"}</div>
              <div className="pcperf-muted">{stage.complexity?.note || ""}</div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StageBars({ stages }) {
  if (!Array.isArray(stages) || stages.length === 0) {
    return <div className="pcperf-muted">No stage spans.</div>;
  }

  return (
    <div>
      {stages.map((stage) => (
        <div className="pcperf-stage-bar-row" key={stage.stage}>
          <div style={{ width: 140 }}>
            <div>{stage.stage}</div>
            <div className="pcperf-muted">
              {formatMs(stage.durationMs)}
              {stage.pctOfStages != null ? ` · ${stage.pctOfStages}%` : ""}
            </div>
          </div>
          <div className="pcperf-stage-bar-track">
            <div
              className="pcperf-stage-bar-fill"
              style={{ width: `${Math.min(100, stage.pctOfStages || 0)}%` }}
            />
          </div>
          <span className="pcperf-bound">
            {boundLabel(stage.complexity?.bound)}
          </span>
        </div>
      ))}
    </div>
  );
}

function SpanTree({ spans, depth = 0 }) {
  if (!Array.isArray(spans) || spans.length === 0) {
    return <div className="pcperf-muted">No nested timing tree recorded.</div>;
  }

  return (
    <div style={{ marginLeft: depth * 14 }}>
      {spans.map((span, index) => (
        <div key={`${span.name}-${index}`}>
          <div className="pcperf-stage-bar-row">
            <div style={{ width: 220 }}>{span.name}</div>
            <div className="pcperf-stage-bar-track">
              <div
                className="pcperf-stage-bar-fill"
                style={{
                  width: `${Math.min(100, Number(span.durationMs || 0) / 10)}%`,
                }}
              />
            </div>
            <span className="pcperf-muted">
              {formatMs(span.durationMs)}
            </span>
          </div>
          {span.children?.length ? (
            <SpanTree spans={span.children} depth={depth + 1} />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function LoanRunDetail({ run }) {
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  return (
    <div className="pcperf-nested">
      <p className="pcperf-nested-title">Run stage totals (sum across tasks)</p>
      <StageBars stages={run.stageBreakdown || []} />

      <p className="pcperf-nested-title" style={{ marginTop: 16 }}>
        Tasks in this run ({run.taskCount})
      </p>
      <table className="pcperf-table">
        <thead>
          <tr>
            <th>Task</th>
            <th>Status</th>
            <th>Duration</th>
            <th>Dominant stage</th>
            <th>Completed</th>
            <th>Input meta</th>
          </tr>
        </thead>
        <tbody>
          {(run.tasks || []).map((task) => (
            <React.Fragment key={`${run.runId}-${task.taskId}-${task.id}`}>
              <tr
                className="clickable"
                onClick={() =>
                  setExpandedTaskId((current) =>
                    current === task.id ? null : task.id,
                  )
                }
              >
                <td>
                  <div>{task.taskTitle}</div>
                  <div className="pcperf-muted">{task.taskId}</div>
                </td>
                <td>{task.status || "—"}</td>
                <td>{formatMs(task.durationMs)}</td>
                <td>
                  {task.dominantStage ? (
                    <span className="pcperf-stage-chip">
                      {task.dominantStage.stage} (
                      {formatMs(task.dominantStage.durationMs)})
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="pcperf-muted">
                  {formatTimestamp(task.completedAt)}
                </td>
                <td className="pcperf-muted">
                  {task.inputMeta && Object.keys(task.inputMeta).length
                    ? JSON.stringify(task.inputMeta)
                    : "—"}
                </td>
              </tr>
              {expandedTaskId === task.id ? (
                <tr>
                  <td colSpan={6}>
                    <div className="pcperf-nested">
                      <p className="pcperf-nested-title">Stage spans</p>
                      <StageBars stages={task.stages || []} />
                      <p className="pcperf-nested-title" style={{ marginTop: 16 }}>
                        Nested timing tree
                      </p>
                      <SpanTree spans={task.spanTree} />
                      {task.incomeToolCalls?.length ? (
                        <>
                          <p
                            className="pcperf-nested-title"
                            style={{ marginTop: 16 }}
                          >
                            Income calculator calls
                          </p>
                          <table className="pcperf-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Calculator</th>
                                <th>Duration</th>
                                <th>Result</th>
                              </tr>
                            </thead>
                            <tbody>
                              {task.incomeToolCalls.map((call) => (
                                <tr key={`${task.id}-${call.index}`}>
                                  <td>{call.index}</td>
                                  <td>{call.name}</td>
                                  <td>{formatMs(call.durationMs)}</td>
                                  <td>{call.success ? "Success" : "Failed"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LoanRunsPanel({ organizationId, loanId, env }) {
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedRunId, setExpandedRunId] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId || !loanId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchLoanRuns({
        organizationId,
        loanId,
        limit,
        env,
      });
      setData(result);
      setExpandedRunId(result?.runs?.[0]?.runId || null);
    } catch (err) {
      setError(err?.message || "Failed to load loan runs.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId, loanId, limit, env]);

  useEffect(() => {
    load();
  }, [load]);

  const runs = data?.runs || [];

  return (
    <div className="pcperf-nested">
      <div className="pcperf-loan-controls" style={{ borderBottom: "none", padding: "0 0 12px" }}>
        <select
          className="pcperf-select"
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          aria-label="Runs to load for this loan"
        >
          {LOAN_RUN_LIMITS.map((value) => (
            <option key={value} value={value}>
              Last {value} runs on this loan
            </option>
          ))}
        </select>
        <button
          type="button"
          className="pcperf-btn"
          onClick={load}
          disabled={loading}
        >
          {loading ? <span className="pcperf-spinner" /> : "Refresh runs"}
        </button>
      </div>

      {error ? <div className="pcperf-banner error">{error}</div> : null}

      <table className="pcperf-table">
        <thead>
          <tr>
            <th>Completed</th>
            <th>Run ID</th>
            <th>Type</th>
            <th>Tasks</th>
            <th>Wall (slowest)</th>
            <th>Active compute</th>
            <th>Top stage</th>
          </tr>
        </thead>
        <tbody>
          {loading && runs.length === 0 ? (
            <tr>
              <td colSpan={7} className="pcperf-muted">
                Loading runs…
              </td>
            </tr>
          ) : null}
          {!loading && runs.length === 0 ? (
            <tr>
              <td colSpan={7} className="pcperf-muted">
                No runs found for this loan in the performance store yet.
              </td>
            </tr>
          ) : null}
          {runs.map((run) => (
            <React.Fragment key={run.runId}>
              <tr
                className="clickable"
                onClick={() =>
                  setExpandedRunId((current) =>
                    current === run.runId ? null : run.runId,
                  )
                }
              >
                <td>{formatTimestamp(run.completedAt)}</td>
                <td className="pcperf-muted">{run.runId}</td>
                <td>
                  {run.runType || "—"} / {run.triggerType || "—"}
                </td>
                <td>{formatNumber(run.taskCount, 0)}</td>
                <td>{formatMs(run.wallClockMs)}</td>
                <td>{formatMs(run.activeComputeMs)}</td>
                <td>
                  {run.stageBreakdown?.[0] ? (
                    <span className="pcperf-stage-chip">
                      {run.stageBreakdown[0].stage} (
                      {formatMs(run.stageBreakdown[0].durationMs)})
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
              {expandedRunId === run.runId ? (
                <tr>
                  <td colSpan={7}>
                    <LoanRunDetail run={run} />
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentLoansFeed({ env, days, organizationId }) {
  const [pageSize, setPageSize] = useState(10);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextSkip, setNextSkip] = useState(null);
  const [expandedLoanKey, setExpandedLoanKey] = useState(null);

  const loadPage = useCallback(
    async ({ append }) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const result = await fetchRecentLoans({
          days,
          organizationId: organizationId || "",
          limit: pageSize,
          skip: append ? nextSkip || loans.length : 0,
          env,
        });
        const pageLoans = result?.loans || [];
        setLoans((current) => (append ? [...current, ...pageLoans] : pageLoans));
        setHasMore(Boolean(result?.pagination?.hasMore));
        setNextSkip(result?.pagination?.nextSkip ?? null);
        if (!append && pageLoans[0]) {
          setExpandedLoanKey(
            `${pageLoans[0].organizationId}__${pageLoans[0].loanId}`,
          );
        }
      } catch (err) {
        setError(err?.message || "Failed to load recent loans.");
        if (!append) {
          setLoans([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [days, organizationId, pageSize, env, nextSkip, loans.length],
  );

  useEffect(() => {
    setLoans([]);
    setNextSkip(null);
    setExpandedLoanKey(null);
    loadPage({ append: false });
    // Intentionally reload when filters change, not when loadPage identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, organizationId, pageSize, env]);

  return (
    <div className="pcperf-panel">
      <div className="pcperf-panel-header">
        Recent loans — what just ran
      </div>
      <div className="pcperf-loan-controls">
        <span className="pcperf-muted">
          Newest checklist activity first. Uses the same window / org filter as
          the overview above.
        </span>
        <select
          className="pcperf-select"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          aria-label="Loans per page"
        >
          {LOAN_RUN_LIMITS.map((value) => (
            <option key={value} value={value}>
              {value} at a time
            </option>
          ))}
        </select>
        <button
          type="button"
          className="pcperf-btn"
          onClick={() => loadPage({ append: false })}
          disabled={loading}
        >
          {loading ? <span className="pcperf-spinner" /> : "Refresh"}
        </button>
      </div>

      {error ? (
        <div className="pcperf-banner error" style={{ margin: 16 }}>
          {error}
        </div>
      ) : null}

      <div className="pcperf-panel-body">
        <table className="pcperf-table">
          <thead>
            <tr>
              <th>Last activity</th>
              <th>Organization</th>
              <th>Loan</th>
              <th>Last run wall</th>
              <th>Last run compute</th>
              <th>Tasks in last run</th>
              <th>Top stage</th>
              <th>Runs seen</th>
            </tr>
          </thead>
          <tbody>
            {loading && loans.length === 0 ? (
              <tr>
                <td colSpan={8} className="pcperf-muted">
                  Loading recent loans…
                </td>
              </tr>
            ) : null}
            {!loading && loans.length === 0 ? (
              <tr>
                <td colSpan={8} className="pcperf-muted">
                  No loans with timed checklist runs in this window yet.
                </td>
              </tr>
            ) : null}
            {loans.map((loan) => {
              const key = `${loan.organizationId}__${loan.loanId}`;
              const lastRun = loan.lastRun;

              return (
                <React.Fragment key={key}>
                  <tr
                    className="clickable"
                    onClick={() =>
                      setExpandedLoanKey((current) =>
                        current === key ? null : key,
                      )
                    }
                  >
                    <td>{formatTimestamp(loan.lastCompletedAt)}</td>
                    <td className="pcperf-muted">{loan.organizationId}</td>
                    <td>
                      <div>{loan.loanId}</div>
                      <div className="pcperf-muted">
                        {lastRun
                          ? `${lastRun.runType || "—"} / ${lastRun.triggerType || "—"}`
                          : "—"}
                      </div>
                    </td>
                    <td>{formatMs(lastRun?.wallClockMs)}</td>
                    <td>{formatMs(lastRun?.activeComputeMs)}</td>
                    <td>{formatNumber(lastRun?.taskCount || 0, 0)}</td>
                    <td>
                      {lastRun?.topStage ? (
                        <span className="pcperf-stage-chip">
                          {lastRun.topStage.stage} (
                          {formatMs(lastRun.topStage.durationMs)})
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      {formatNumber(loan.checklistRunCount || 0, 0)}
                      <div className="pcperf-muted">
                        avg task {formatMs(loan.taskDuration?.avgMs)}
                      </div>
                    </td>
                  </tr>
                  {expandedLoanKey === key ? (
                    <tr>
                      <td colSpan={8}>
                        <LoanRunsPanel
                          organizationId={loan.organizationId}
                          loanId={loan.loanId}
                          env={env}
                        />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMore ? (
        <div style={{ padding: 16, display: "flex", justifyContent: "center" }}>
          <button
            type="button"
            className="pcperf-btn pcperf-btn-primary"
            onClick={() => loadPage({ append: true })}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <span className="pcperf-spinner" />
            ) : (
              `Load ${pageSize} more loans`
            )}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function PcPerformancePage({ onReady }) {
  const [theme] = useDarkMode();
  const isDark = theme === "dark";

  const [days, setDays] = useState(7);
  const [env, setEnv] = useState(DEFAULT_ENV);
  const [organizationId, setOrganizationId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedTaskId, setExpandedTaskId] = useState(null);

  useEffect(() => {
    onReady?.();
  }, [onReady]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPerformanceReport({
        days,
        organizationId: organizationId.trim(),
        env,
      });
      setData(result);
    } catch (err) {
      setError(err?.message || "Failed to load PC performance report.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, organizationId, env]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const tasks = data?.tasks || [];
  const stages = data?.stages || [];
  const bottlenecks = data?.bottlenecks || [];
  const sampledRuns = data?.window?.sampledRuns ?? 0;
  const sampleLimit = data?.window?.sampleLimit;

  const stageCatalog = useMemo(() => {
    const complexity = data?.stageComplexity || {};
    const primary = data?.primaryStages || Object.keys(complexity);

    return primary
      .filter((stage) => stage !== "_task_total")
      .map((stage) => ({
        stage,
        label: stage,
        complexity: complexity[stage] || null,
      }));
  }, [data]);

  return (
    <div className={`pcperf-page${isDark ? " pcperf-dark" : ""}`}>
      <div className="pcperf-header">
        <div>
          <h1 className="pcperf-title">PC Performance</h1>
          <p className="pcperf-subtitle">
            Processing checklist runtime by task and nested stage. Use this to
            find average vs high-percentile bottlenecks across prod and staging
            (dev).
          </p>
        </div>
        <div className="pcperf-controls">
          <div className="pcperf-toggle" role="group" aria-label="Environment">
            {ENVS.map((value) => (
              <button
                key={value}
                type="button"
                className={env === value ? "active" : ""}
                onClick={() => setEnv(value)}
              >
                {value === "dev" ? "STAGING" : value.toUpperCase()}
              </button>
            ))}
          </div>
          <select
            className="pcperf-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Time window"
          >
            {DAY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            className="pcperf-input"
            placeholder="Filter org ID (optional)"
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            style={{ minWidth: 220 }}
          />
          <button
            type="button"
            className="pcperf-btn pcperf-btn-primary"
            onClick={load}
            disabled={loading}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            {loading ? (
              <span className="pcperf-spinner" />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="pcperf-banner error">{error}</div> : null}

      {!error && data && sampledRuns === 0 ? (
        <div className="pcperf-banner info">
          No timed checklist runs in this window yet. After deploy, new task
          runs write stage timings to `processingChecklistPerformanceRuns`.
        </div>
      ) : null}

      {sampleLimit && sampledRuns >= sampleLimit ? (
        <div className="pcperf-banner info">
          Result set hit the query cap ({sampleLimit}) and may be truncated.
          Narrow the window or filter by organization.
        </div>
      ) : null}

      <div className="pcperf-cards">
        <MetricCard label="Samples" value={formatNumber(sampledRuns, 0)} />
        <MetricCard label="Avg task" value={formatMs(summary.avgMs)} />
        <MetricCard label="P90 task" value={formatMs(summary.p90Ms)} />
        <MetricCard label="P95 task" value={formatMs(summary.p95Ms)} />
        <MetricCard label="Max task" value={formatMs(summary.maxMs)} />
        <MetricCard
          label="Unique tasks"
          value={formatNumber(summary.uniqueTasks || 0, 0)}
        />
      </div>

      <RecentLoansFeed
        env={env}
        days={days}
        organizationId={organizationId.trim()}
      />

      <div className="pcperf-panel">
        <div className="pcperf-panel-header">Top stage bottlenecks</div>
        <div className="pcperf-panel-body">
          <table className="pcperf-table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>Count</th>
                <th>Avg</th>
                <th>P90</th>
                <th>P95</th>
                <th>Max</th>
                <th>Bound</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {bottlenecks.length === 0 ? (
                <tr>
                  <td colSpan={8} className="pcperf-muted">
                    No bottlenecks yet.
                  </td>
                </tr>
              ) : (
                bottlenecks.map((item) => (
                  <tr key={item.stage}>
                    <td>
                      <div>{item.stage}</div>
                    </td>
                    <td>{formatNumber(item.count, 0)}</td>
                    <td>{formatMs(item.avgMs)}</td>
                    <td>{formatMs(item.p90Ms)}</td>
                    <td>{formatMs(item.p95Ms)}</td>
                    <td>{formatMs(item.maxMs)}</td>
                    <td>
                      <span className="pcperf-bound">
                        {boundLabel(item.bound)}
                      </span>
                    </td>
                    <td>
                      <div>{item.bigO || ""}</div>
                      <div className="pcperf-muted">{item.note || ""}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pcperf-panel">
        <div className="pcperf-panel-header">
          Tasks (sorted by average runtime)
        </div>
        <div className="pcperf-panel-body">
          <table className="pcperf-table">
            <thead>
              <tr>
                <th>Task</th>
                <th>Count</th>
                <th>Avg</th>
                <th>P90</th>
                <th>P95</th>
                <th>Max</th>
                <th>Dominant stage</th>
                <th>Sample input meta</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan={8} className="pcperf-muted">
                    No task samples.
                  </td>
                </tr>
              ) : (
                tasks.map((task) => (
                  <React.Fragment key={task.taskId}>
                    <tr
                      className="clickable"
                      onClick={() =>
                        setExpandedTaskId((current) =>
                          current === task.taskId ? null : task.taskId,
                        )
                      }
                    >
                      <td>
                        <div>{task.taskTitle || task.taskId}</div>
                        <div className="pcperf-muted">{task.taskId}</div>
                      </td>
                      <td>{formatNumber(task.runCount, 0)}</td>
                      <td>{formatMs(task.duration?.avgMs)}</td>
                      <td>{formatMs(task.duration?.p90Ms)}</td>
                      <td>{formatMs(task.duration?.p95Ms)}</td>
                      <td>{formatMs(task.duration?.maxMs)}</td>
                      <td>
                        {task.dominantStage ? (
                          <span className="pcperf-stage-chip">
                            {task.dominantStage.stage} (
                            {formatMs(task.dominantStage.avgMs)})
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="pcperf-muted">
                        {task.sampleInputMeta
                          ? JSON.stringify(task.sampleInputMeta)
                          : "—"}
                      </td>
                    </tr>
                    {expandedTaskId === task.taskId ? (
                      <tr>
                        <td colSpan={8}>
                          <TaskStages stages={task.stages} />
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pcperf-panel">
        <div className="pcperf-panel-header">Stages across all tasks</div>
        <div className="pcperf-panel-body">
          <TaskStages stages={stages} />
        </div>
      </div>

      <div className="pcperf-panel">
        <div className="pcperf-panel-header">Stage catalog / complexity</div>
        <div className="pcperf-panel-body">
          <table className="pcperf-table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>Bound</th>
                <th>Big O</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {stageCatalog.map((stage) => (
                <tr key={stage.stage}>
                  <td>
                    <div>{stage.label}</div>
                    <div className="pcperf-muted">{stage.stage}</div>
                  </td>
                  <td>
                    <span className="pcperf-bound">
                      {boundLabel(stage.complexity?.bound)}
                    </span>
                  </td>
                  <td>{stage.complexity?.bigO || "—"}</td>
                  <td className="pcperf-muted">
                    {stage.complexity?.note || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
